/**
 * APRE-10 (Autoregulatory Progressive Resistance Exercise) — Mann protocol
 * ========================================================================
 *
 * Reference: Mann JB, Thyfault JP, Ivey PA, Sayers SP (2010), Journal of
 *   Strength and Conditioning Research.
 *
 * Protocol (APRE-10, for hypertrophy):
 *
 *   Set 1:  50% of 10RM  ×  10 reps   (warmup)
 *   Set 2:  75% of 10RM  ×   6 reps   (warmup)
 *   Set 3: 100% of 10RM  × max reps   (working set — go to failure-ish)
 *   Set 4: ADJUSTED weight based on Set 3's actual reps, × max reps
 *
 * After Set 4: the user's 10RM is updated for the NEXT session. As of
 * Feb 2026, the update is WEIGHT-AWARE: we observe an Epley-derived 10RM
 * from the user's ACTUAL logged (weight, reps) on Set 4 and blend with
 * the stored 10RM. The pure Mann reps-only table is still exposed via
 * `apreNewRm()` for legacy callers / unit tests, but the production
 * path uses `apreNewRmFromActual()`.
 *
 * Storage model (Feb 2026, post-refactor):
 *   plan.apreExerciseRm: Record<exerciseName, {
 *                          rm, unit, loadIncrement?, lastUpdated
 *                        }>
 *                        SINGLE SOURCE OF TRUTH for the 10RM of each
 *                        lift. Same lift on multiple training days
 *                        shares ONE rm value.
 *   exercise.apre:       { enabled, mode, unit, rm (legacy fallback),
 *                          lastUpdated }
 *                        `rm` here is preserved as a read-only fallback
 *                        for plans created before the plan-level map.
 *
 * Why this exists (the bug the old `apreNewRm()` had):
 *   The prescribed weight was treated as ground truth. If the user
 *   manually dialed Set 4 weight down (e.g. prescribed 82.5, did 70 ×
 *   8 because the bar felt heavy), the Mann delta on REPS alone said
 *   "+inc" and the algorithm prescribed 82.5+inc next session. The fix
 *   observes the actual lifted load.
 *
 * The module is invisible by design — no banners, popups, or extra UI
 * during a workout. The user only sees the weight numbers change.
 */
import { getExerciseIncrement, roundToIncrement, Unit } from './loadIncrement';
/** Iter308 — APRE load/RM decisions use FULL reps only.
 *  Owner rule (Sep 2026, extended from DUP): lengthened partials are a
 *  fatigue tool, not proof the load is too light, so half-reps must never
 *  inflate the 10RM or Set 4's prescription. They still count for volume,
 *  statistics and AI analysis. */
const _fullReps = (set: any): number => Math.round(parseFloat(String(set?.reps ?? '')) || 0);

// Type for the per-exercise APRE config stored on the exercise object.
// Mongo is schema-flexible so this just lives at `exercise.apre`.
export interface ApreConfig {
  enabled: boolean;       // user toggle
  mode: 'apre-10';        // future-proofed for apre-6 / apre-3
  rm: number;             // legacy fallback — canonical rm lives at
                          // plan.apreExerciseRm[name] now
  unit?: Unit;            // unit at the time the RM was set
  lastUpdated?: string;   // ISO timestamp of the last auto-update
}

/** Adjust a weight by the Mann table given the reps just performed.
 *  The size of each "+1 zone" bump is one `inc` — the exercise's smallest
 *  loadable increment (per-exercise, defaults to 2.5 kg / 5 lb). */
export function apreAdjustment(repsPerformed: number, inc: number): number {
  if (repsPerformed <= 0 || !inc || inc <= 0) return 0;
  if (repsPerformed <= 4) return -inc * 2;       // too heavy
  if (repsPerformed <= 7) return 0;              // hold
  if (repsPerformed <= 12) return inc;           // top end
  return inc * 2;                                // too light
}

/** Returns the 4 prescribed weights for a given 10RM. Used to PREFILL sets
 *  1-3 before the workout. Set 4 is left blank because it depends on
 *  set 3's actual reps. */
export function apreSetWeights(rm: number, inc: number): Array<number | null> {
  if (!rm || rm <= 0) return [null, null, null, null];
  return [
    roundToIncrement(rm * 0.5, inc),   // set 1: 50%
    roundToIncrement(rm * 0.75, inc),  // set 2: 75%
    roundToIncrement(rm, inc),         // set 3: 100%
    null,                               // set 4: depends on set 3 reps
  ];
}

/** Compute Set 4's weight given Set 3's reps. */
export function apreSet4Weight(rm: number, set3Reps: number, inc: number): number {
  const adj = apreAdjustment(set3Reps, inc);
  return roundToIncrement(rm + adj, inc);
}

/** LEGACY: compute next-session 10RM from Set 4 reps ONLY (no weight).
 *  Kept for back-compat with the unit tests that pin Mann's table.
 *  New code paths use `apreNewRmFromActual()`. */
export function apreNewRm(currentRm: number, set4Reps: number, inc: number): number {
  const adj = apreAdjustment(set4Reps, inc);
  return roundToIncrement(currentRm + adj, inc);
}

/**
 * Epley-style 10RM back-calc from a user-entered (weight, reps) pair.
 * Used by the weight-aware update to observe what the user ACTUALLY did
 * vs. what was prescribed.
 *
 * Formula: 1RM = w × (1 + r/30), then 10RM = 1RM / (1 + 10/30).
 */
export function apreObservedRm(weight: number, reps: number, inc: number): number {
  if (weight <= 0 || reps <= 0) return 0;
  const oneRm = weight * (1 + reps / 30);
  const tenRm = oneRm / (1 + 10 / 30);
  return roundToIncrement(tenRm, inc);
}

/**
 * Weight-aware update (Feb 2026 redesign, v2 after real-user audit).
 *
 * Compute the NEXT-session 10RM using TWO ground truths from Set 4:
 *   • `actualWeight` — what the user actually lifted on Set 4
 *   • `actualReps`   — Set 4's AMRAP performance
 *
 * Rule (mirrors the DUP fix — see dup.ts for the full rationale):
 *   1. Mann's adjustment on Set 4 reps. Table unchanged.
 *   2. If Mann adj === 0 → return `actualWeight` EXACTLY (the user's
 *      last-lifted weight becomes the new anchor). Previously this
 *      returned `currentRm`, which caused the "I lifted 102.5×6 but
 *      the RM stayed at 100" class of bugs when the user manually
 *      overrode the prefill upward and landed in the hold zone
 *      (5–7 reps). Same behaviour as DUP — self-correcting.
 *   3. If Mann adj !== 0 → new 10RM = `actualWeight + adj`, rounded to
 *      the exercise's smallest loadable increment. Anchored on what
 *      the user actually lifted, not on the stored prescription.
 */
export function apreNewRmFromActual(
  currentRm: number,
  actualWeight: number,
  actualReps: number,
  inc: number,
): number {
  if (!actualWeight || actualWeight <= 0 || !actualReps || actualReps <= 0) {
    return currentRm > 0 ? roundToIncrement(currentRm, inc) : 0;
  }
  const adj = apreAdjustment(actualReps, inc);
  // Iter226: round on the adj===0 path too. During deload we back-scale
  // observed weight to 100 % domain (÷ 0.75), which produces non-grid
  // values (e.g. 82.5 / 0.75 = 110 — fine, but 80 / 0.75 = 106.67 —
  // dirty). Rounding here keeps the canonical rm on the plate grid.
  if (adj === 0) return roundToIncrement(actualWeight, inc);
  return roundToIncrement(Math.max(inc, actualWeight + adj), inc);
}

/** Read the stored 10RM for an exercise (presence-check only).
 *  Returns null when APRE is disabled. The actual `rm` returned here is
 *  the legacy per-exercise value if present; callers should use
 *  `getApreRm(plan, exercise)` to get the canonical plan-level rm. */
export function getExerciseApreConfig(exercise: any): ApreConfig | null {
  const cfg = exercise?.apre;
  if (!cfg || !cfg.enabled) return null;
  const legacyRm = typeof cfg.rm === 'number' && cfg.rm > 0 ? cfg.rm : 0;
  return {
    enabled: true,
    mode: cfg.mode || 'apre-10',
    rm: legacyRm,
    unit: cfg.unit || 'kg',
    lastUpdated: cfg.lastUpdated,
  };
}

// -- Plan-level RM map helpers -------------------------------------------

interface PlanRmEntry {
  rm: number;
  unit: Unit;
  loadIncrement?: number;
  lastUpdated: string;
}

function exerciseKey(exercise: any): string | null {
  const n = exercise?.name;
  return typeof n === 'string' && n.trim().length > 0 ? n.trim() : null;
}

/** Read the canonical 10RM for this exercise. */
export function getApreRm(plan: any, exercise: any): number {
  const key = exerciseKey(exercise);
  const map = plan?.apreExerciseRm;
  if (key && map && typeof map === 'object') {
    const entry = map[key];
    if (entry && typeof entry.rm === 'number' && entry.rm > 0) return entry.rm;
  }
  const legacy = Number(exercise?.apre?.rm);
  if (legacy > 0) return legacy;
  return 0;
}

/** Write the canonical 10RM to the plan-level map. Mutates `plan`. */
export function setApreRm(
  plan: any,
  exercise: any,
  rm: number,
  opts: { unit?: Unit; loadIncrement?: number } = {},
): void {
  if (!plan || !exercise) return;
  const key = exerciseKey(exercise);
  if (!key || rm <= 0) return;
  if (!plan.apreExerciseRm || typeof plan.apreExerciseRm !== 'object') {
    plan.apreExerciseRm = {};
  }
  const now = new Date().toISOString();
  plan.apreExerciseRm[key] = {
    rm,
    unit: opts.unit || exercise?.apre?.unit || 'kg',
    loadIncrement: opts.loadIncrement || exercise?.loadIncrement,
    lastUpdated: now,
  };
  if (exercise.apre) {
    exercise.apre = { ...exercise.apre, lastUpdated: now };
  }
}

/** One-shot migration of legacy per-exercise rm → plan-level map. */
export function migrateApreRmToPlan(plan: any): boolean {
  if (!plan?.dayExercises) return false;
  if (!plan.apreExerciseRm || typeof plan.apreExerciseRm !== 'object') {
    plan.apreExerciseRm = {};
  }
  let migrated = false;
  const candidates: Record<string, PlanRmEntry> = {};
  Object.keys(plan.dayExercises).forEach(dayKey => {
    const list = plan.dayExercises[dayKey] || [];
    list.forEach((ex: any) => {
      const key = exerciseKey(ex);
      const legacy = Number(ex?.apre?.rm);
      if (!key || !ex?.apre?.enabled || !(legacy > 0)) return;
      const lastUpdated = ex?.apre?.lastUpdated || new Date(0).toISOString();
      const current = candidates[key];
      if (!current || lastUpdated > current.lastUpdated || (lastUpdated === current.lastUpdated && legacy > current.rm)) {
        candidates[key] = {
          rm: legacy,
          unit: ex?.apre?.unit === 'lb' ? 'lb' : 'kg',
          loadIncrement: typeof ex?.loadIncrement === 'number' ? ex.loadIncrement : undefined,
          lastUpdated,
        };
      }
    });
  });
  Object.keys(candidates).forEach(key => {
    if (!plan.apreExerciseRm[key] || !(Number(plan.apreExerciseRm[key].rm) > 0)) {
      plan.apreExerciseRm[key] = candidates[key];
      migrated = true;
    }
  });
  return migrated;
}

/** Apply APRE prefill to an exercise that has APRE enabled. Mutates the
 *  exercise's sets in-place so callers can savePlanToStorage normally.
 *  - Forces exactly 4 sets (Mann's protocol)
 *  - Prefills weight on sets 1-3, leaves set 4 blank (depends on set 3)
 *  - Prefills target reps (10 / 6 / max / max) so the user has guidance
 *    in the reps input field
 *  - Skips any set that's already done (don't clobber logged work)
 *
 *  `plan` is now required so we can read the canonical 10RM from the
 *  plan-level map. Passing `null`/`undefined` falls back to the legacy
 *  `exercise.apre.rm` for back-compat with pre-Feb 2026 callers.
 */
export function applyApreToExercise(
  exercise: any,
  plan?: any,
  opts?: { keepSetCount?: boolean },
): void {
  const cfg = getExerciseApreConfig(exercise);
  if (!cfg) return;
  const rm = getApreRm(plan, exercise);

  // Ensure exactly 4 sets — APRE is a strict 4-set protocol.
  //
  // iter285 (production bug: "app deletes the sets I add"): callers that only
  // want the reps/weight guidance filled in — e.g. the plan-load self-heal —
  // pass `keepSetCount` so a 5th set the user added by hand survives.
  if (!exercise.sets) exercise.sets = [];
  if (!opts?.keepSetCount) {
    while (exercise.sets.length < 4) {
      exercise.sets.push({ done: false, type: 'Regular' });
    }
    // Trim excess sets only if they aren't done.
    while (exercise.sets.length > 4 && !exercise.sets[exercise.sets.length - 1].done) {
      exercise.sets.pop();
    }
  }

  const inc = getExerciseIncrement(exercise, cfg.unit);
  // Deload-week aware: APRE sets are prefilled at render time from the
  // canonical 10RM, so they miss the 25% deload cut that `usePlanLoader`
  // applies to `set.weight`. Compute the deload-adjusted anchor here so
  // sets 1-3 (and Set 4 via `apreSet4Weight`) all shrink together.
  // Non-APRE exercises are unaffected — they carry the deload weight in
  // `set.weight` directly. iter189 (production bug: APRE has no deload).
  // iter251d: also treat manually-triggered deload weeks the same way.
  const cw = plan?.currentWeek;
  const isDeloadWeek = !!plan && (
    (plan.hasDeloadWeek && cw === plan.deloadWeek)
    || (Array.isArray(plan.manualDeloadWeeks) && plan.manualDeloadWeeks.includes(cw))
  );
  const effectiveRm = isDeloadWeek ? rm * 0.75 : rm;
  const weights = rm > 0 ? apreSetWeights(effectiveRm, inc) : [null, null, null, null];
  const targetReps = ['10', '6', 'max', 'max'];

  for (let i = 0; i < 4 && i < exercise.sets.length; i++) {
    const set = exercise.sets[i];
    if (set.done) continue; // don't touch what's already logged

    // Set weight only when set is empty (preserves user manual edits).
    if (i < 3 && (set.weight === undefined || set.weight === '' || set.weight === null)) {
      set.weight = weights[i] !== null ? String(weights[i]) : '';
    }
    // Set 4 stays blank until set 3 is logged — handled in onSetDone.

    // Always set the reps guidance (these are GUIDANCE, the user logs
    // actual). Iter276: written even when there is no 10RM anchor yet —
    // the rep pattern 10/6/max/max never depends on the weight, and the
    // old `if (!(rm > 0)) return` at the top of this function left every
    // reps field blank on APRE exercises whose RM hadn't been seeded.
    if (set.reps === undefined || set.reps === '' || set.reps === null) {
      set.reps = targetReps[i];
    }
  }
}

/** Called when a set is logged as done on an APRE exercise. Returns true
 *  if APRE handled the side-effect.
 *
 *  Behavior:
 *   • Set 3 logged → fill Set 4's weight based on Set 3's reps (Mann)
 *   • Set 4 logged → update the canonical 10RM (plan-level map) using
 *     the weight-aware observation rule.
 *
 *  Signature accepts `plan` so we can read+write the canonical RM. The
 *  legacy 3-arg form `(exercise, setIndex)` is preserved by treating
 *  `plan` as optional — when missing, the function falls back to the
 *  per-exercise `exercise.apre.rm` for both read and write (preserving
 *  pre-Feb 2026 unit-test behaviour).
 */
export function handleApreSetDone(plan: any, exercise: any, setIndex?: number): boolean {
  // Back-compat shim: old callers used `handleApreSetDone(exercise, idx)`
  // (2 args, no plan). Detect that signature shape and re-dispatch.
  if (plan && typeof plan === 'object' && Array.isArray(plan.sets) && typeof exercise === 'number') {
    const oldExercise = plan;
    const oldIdx = exercise as unknown as number;
    return _handleApreSetDoneImpl(null, oldExercise, oldIdx);
  }
  return _handleApreSetDoneImpl(plan, exercise, setIndex as number);
}

function _handleApreSetDoneImpl(plan: any, exercise: any, setIndex: number): boolean {
  const cfg = getExerciseApreConfig(exercise);
  if (!cfg) return false;
  if (!exercise.sets) return false;

  const inc = getExerciseIncrement(exercise, cfg.unit);
  let currentRm = getApreRm(plan, exercise);

  // Iter276 — bootstrap the 10RM from the user's ACTUAL logged set when
  // no anchor exists. APRE exercises can legitimately have rm=0 (the
  // protocol was enabled without the setup dialog, or a restart cleared
  // the map). Before this, the whole protocol was inert: Set 4's weight
  // was never computed and no 10RM was ever stored.
  if (!(currentRm > 0)) {
    const src = exercise.sets[setIndex];
    const srcReps = _fullReps(src);
    const srcWeight = parseFloat(String(src?.weight || '0'));
    if (srcReps > 0 && srcWeight > 0) {
      currentRm = apreObservedRm(srcWeight, srcReps, inc);
      if (currentRm > 0) {
        if (plan) {
          setApreRm(plan, exercise, currentRm, { unit: cfg.unit, loadIncrement: exercise.loadIncrement });
        } else {
          exercise.apre = { ...exercise.apre, rm: currentRm, lastUpdated: new Date().toISOString() };
        }
      }
    }
  }
  if (!(currentRm > 0)) return false;

  // Deload-week aware: during deload we prescribe at 75% of the canonical
  // 10RM (see applyApreToExercise). Set 4 must be computed against that
  // same reduced anchor, and the observed-RM update for Set 4 must scale
  // its observation back UP to the 100% domain before writing the new
  // canonical rm — otherwise the deload week would permanently shrink
  // the user's saved 10RM. iter189.
  // iter251d: also handle manually-triggered deload weeks the same way.
  const cw = plan?.currentWeek;
  const isDeloadWeek = !!plan && (
    (plan.hasDeloadWeek && cw === plan.deloadWeek)
    || (Array.isArray(plan.manualDeloadWeeks) && plan.manualDeloadWeeks.includes(cw))
  );
  const effectiveRm = isDeloadWeek ? currentRm * 0.75 : currentRm;

  if (setIndex === 2) {
    // Set 3 just logged — compute Set 4's weight from Set 3's reps using
    // Mann's table on the CURRENT canonical rm. The user's actual Set 3
    // weight isn't used here (Set 4 is prescriptive, not observational).
    // CRITICAL: always overwrite Set 4's weight (unless it's already
    // logged as done). Carried-over weight from last week MUST NOT block
    // the recompute, otherwise the autoregulation visibly stalls week-
    // to-week.
    const set3 = exercise.sets[2];
    // Full reps only (iter308) — partials don't drive the prescription.
    const reps = _fullReps(set3);
    if (reps > 0) {
      const set4Weight = apreSet4Weight(effectiveRm, reps, inc);
      const set4 = exercise.sets[3];
      if (set4 && !set4.done) {
        set4.weight = String(set4Weight);
        if (!set4.reps || set4.reps === '') set4.reps = 'max';
      }
      return true;
    }
  }

  if (setIndex === 3) {
    // Set 4 just logged — update the canonical 10RM via the weight-aware
    // observation rule. Falls back to Set 3's reps/weight when the user
    // left Set 4 placeholders untouched.
    const set4 = exercise.sets[3];
    let reps = _fullReps(set4);
    let weight = parseFloat(String(set4?.weight || '0'));
    if (!reps || reps <= 0 || Number.isNaN(reps)) {
      const set3 = exercise.sets[2];
      reps = _fullReps(set3);
    }
    if (!weight || weight <= 0 || Number.isNaN(weight)) {
      const set3 = exercise.sets[2];
      weight = parseFloat(String(set3?.weight || '0'));
    }
    if (reps > 0 && weight > 0) {
      // During deload, the observed (weight, reps) reflect a 75% training
      // load. Scale the weight back to 100% so the canonical rm update
      // reflects the user's true capability, not the intentional
      // deload cut.
      const observedWeight = isDeloadWeek ? weight / 0.75 : weight;
      const newRm = apreNewRmFromActual(currentRm, observedWeight, reps, inc);
      if (plan) {
        setApreRm(plan, exercise, newRm, { unit: cfg.unit, loadIncrement: exercise.loadIncrement });
      } else {
        // No plan ref provided → mirror to the legacy field so older
        // call-sites (and the unit tests written before this refactor)
        // keep observing a state change. Production code paths always
        // pass `plan`.
        exercise.apre = {
          ...exercise.apre,
          rm: newRm,
          lastUpdated: new Date().toISOString(),
        };
      }
      return true;
    }
  }

  return false;
}

/** Reset APRE sets for a new week (clears weights so the next week's
 *  prefill picks up any RM updates from the previous week's Set 4). */
export function resetApreSetsForNewWeek(exercise: any): void {
  const cfg = getExerciseApreConfig(exercise);
  if (!cfg) return;
  if (!exercise.sets) return;
  // Clear weights and reps so applyApreToExercise() refills with the new RM.
  for (let i = 0; i < exercise.sets.length; i++) {
    exercise.sets[i] = { done: false, type: 'Regular' };
  }
}

/**
 * Reset APRE sets for a new week (clears weights so the next week's
 * prefill picks up any RM updates from the previous week's Set 4).
 * See `resetApreSetsForNewWeek` above. `resyncApreIfStale` was removed
 * in iter225 (was clobbering user weight overrides on refresh).
 */

export const APRE_INFO_TEXT =
  'APRE 10 (Mann protocol): 4 sets — 50% × 10, 75% × 6, 100% × max reps, then Set 4 weight auto-adjusts from Set 3 reps. The app silently updates your 10RM each session. Best for compound lifts (squat, bench, deadlift, OHP).';
